(() => {
const PRODUCT_INFO_CACHE_LIMIT = 60;
const PLP_IMAGE_WIDTH_DESKTOP = 640;
const PLP_IMAGE_WIDTH_MOBILE = 320;
const PLP_THUMB_WIDTH = 120;
const PLP_THUMB_HEIGHT = 60;
const COUNTRY_TO_CURRENCY_FALLBACK = {
    US: "USD",
    CA: "CAD",
    GB: "GBP",
    UK: "GBP",
    JP: "JPY",
};
const productInfoCache = new Map();
let lastPlpGroupsRaw = "";

const poPlpCardSortDebugEnabled = () => {
    if (typeof window === "undefined") return false;

    try {
        const url = new URL(window.location.href);
        const debugParam = url.searchParams.get("po_sort_debug");

        if (debugParam === "1" || debugParam === "true") {
            sessionStorage.setItem("po_sort_debug", "1");
            window.__PO_PLP_SORT_DEBUG__ = true;
            return true;
        }

        if (debugParam === "0" || debugParam === "false") {
            sessionStorage.removeItem("po_sort_debug");
            window.__PO_PLP_SORT_DEBUG__ = false;
            return false;
        }
    } catch (_error) {
        // no-op
    }

    if (window.__PO_PLP_SORT_DEBUG__ === true) {
        return true;
    }

    try {
        return sessionStorage.getItem("po_sort_debug") === "1";
    } catch (_error) {
        return false;
    }
};

const poPlpCardSortDebug = (scope, payload = {}) => {
    if (!poPlpCardSortDebugEnabled()) {
        return;
    }

    window.poPlpSortDebugTools?.record("card", scope, payload);
    console.log(`[PO sort debug][card] ${scope}`, payload);
};

const normalizeIndexName = (value) => (value || "").toString().trim().toLowerCase();
const normalizeCollectionHandle = (value) => (value || "").toString().trim().toLowerCase();

const getCurrentPlpIndexName = () =>
    normalizeIndexName(
        window.__poPlpCurrentIndexName ||
        window.algoliaPoCustomSearch?.loadMoreIndex ||
        document.querySelector("input[name='sort_by']:checked")?.value ||
        document.querySelector(".po-original-sort .ais-SortBy-select")?.value ||
        window.poAlgoliaUtils?.getLastIndexName?.(null) ||
        window.algoliaShopify?.instantsearch?.search?.helper?.lastResults?.index
    );

const isPriceSortIndexName = (value) => {
    const normalized = normalizeIndexName(value);
    if (!normalized) return false;

    return (
        normalized.includes("price_asc") ||
        normalized.includes("price_desc") ||
        normalized.includes(".price_asc") ||
        normalized.includes(".price_desc")
    );
};

const getPriceSortDirection = (value = getCurrentPlpIndexName()) => {
    const normalized = normalizeIndexName(value);
    if (!normalized) return null;

    if (normalized.includes("price_desc") || normalized.includes(".price_desc")) {
        return "desc";
    }

    if (normalized.includes("price_asc") || normalized.includes(".price_asc")) {
        return "asc";
    }

    return null;
};

const normalizeCountryCode = (value) => {
    if (typeof value !== "string") return null;
    const trimmed = value.trim();
    return /^[A-Z]{2}$/i.test(trimmed) ? trimmed.toUpperCase() : null;
};

const normalizeCurrencyCode = (value) => {
    if (typeof value !== "string") return null;
    const trimmed = value.trim();
    return /^[A-Z]{3}$/i.test(trimmed) ? trimmed.toUpperCase() : null;
};

const getActiveCurrencyCode = () => {
    const htmlLangCountry = document?.documentElement?.lang?.split?.("-")?.[1];
    const localeCountry = window.SHOP_LOCALES?.current?.iso_code?.split?.("-")?.[1];
    const countryCandidates = [
        window.Shopify?.country,
        document?.documentElement?.dataset?.countryCode,
        htmlLangCountry,
        localeCountry,
    ];

    const currencyCandidates = [
        window.Shopify?.currency?.active,
        window.Shopify?.currency?.currency_code,
        window.Shopify?.currency?.iso_code,
        window.ShopifyAnalytics?.meta?.currency,
        window.Shopify?.analytics?.meta?.currency,
        document?.documentElement?.dataset?.currencyCode,
    ];

    for (const candidate of currencyCandidates) {
        const normalized = normalizeCurrencyCode(candidate);
        if (normalized) return normalized;
    }

    for (const candidate of countryCandidates) {
        const normalizedCountry = normalizeCountryCode(candidate);
        if (!normalizedCountry) continue;
        const fallbackCurrency = COUNTRY_TO_CURRENCY_FALLBACK[normalizedCountry];
        if (fallbackCurrency) return fallbackCurrency;
    }

    return null;
};

const getComparableHitPriceCandidates = (hit = {}) => {
    const currency = getActiveCurrencyCode()?.toLowerCase?.() || "";
    const marketPricing = currency ? hit?.[`market_pricing_${currency}`] : null;
    const directCandidates = [
        marketPricing?.price,
        marketPricing?.variants_min_price,
        marketPricing?.variants_max_price,
        hit?.price,
        hit?.variants_min_price,
        hit?.variants_max_price,
    ]
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value));

    const rangeCandidates = [
        marketPricing?.price_range,
        hit?.price_range,
    ]
        .flatMap((rangeValue) => String(rangeValue || "").split(":"))
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value));

    return {
        directCandidates,
        rangeCandidates,
    };
};

const getComparableHitPrice = (hit = {}, direction = getPriceSortDirection()) => {
    const { directCandidates, rangeCandidates } = getComparableHitPriceCandidates(hit);
    const positiveDirectCandidates = directCandidates.filter((value) => value > 0);
    const positiveRangeCandidates = rangeCandidates.filter((value) => value > 0);
    const nonZeroDirectCandidates = directCandidates.filter((value) => value !== 0);
    const nonZeroRangeCandidates = rangeCandidates.filter((value) => value !== 0);

    const usableCandidates = positiveDirectCandidates.length > 0
        ? positiveDirectCandidates
        : positiveRangeCandidates.length > 0
            ? positiveRangeCandidates
            : nonZeroDirectCandidates.length > 0
                ? nonZeroDirectCandidates
                : nonZeroRangeCandidates.length > 0
                    ? nonZeroRangeCandidates
                    : [];

    if (usableCandidates.length === 0) {
        return null;
    }

    return direction === "desc"
        ? Math.max(...usableCandidates)
        : Math.min(...usableCandidates);
};

const getDirectHitPrice = (hit = {}, direction = getPriceSortDirection()) => {
    const { directCandidates } = getComparableHitPriceCandidates(hit);
    const positiveDirectCandidates = directCandidates.filter((value) => value > 0);
    const nonZeroDirectCandidates = directCandidates.filter((value) => value !== 0);
    const usableCandidates = positiveDirectCandidates.length > 0
        ? positiveDirectCandidates
        : nonZeroDirectCandidates.length > 0
            ? nonZeroDirectCandidates
            : [];

    if (usableCandidates.length === 0) {
        return null;
    }

    return direction === "desc"
        ? Math.max(...usableCandidates)
        : Math.min(...usableCandidates);
};

const comparePriceSortValues = (left, right, direction = getPriceSortDirection()) => {
    const leftFinite = Number.isFinite(left);
    const rightFinite = Number.isFinite(right);

    if (leftFinite && rightFinite) {
        if (left === right) {
            return 0;
        }

        return direction === "desc" ? right - left : left - right;
    }

    if (leftFinite) return -1;
    if (rightFinite) return 1;
    return 0;
};

const filterNonRxHits = (hits = []) =>
    window.poPlpRxUtils?.filterNonRxHits(hits) ?? (Array.isArray(hits) ? hits.slice() : []);

const sortHitsForPriceDisplay = (hits = [], direction = getPriceSortDirection()) => {
    const rxFilteredHits = filterNonRxHits(hits);

    if (!direction || !Array.isArray(rxFilteredHits) || rxFilteredHits.length < 2) {
        return rxFilteredHits;
    }

    return rxFilteredHits
        .map((hit, originalIndex) => ({
            hit,
            originalIndex,
            comparablePrice: getComparableHitPrice(hit, direction),
            directPrice: getDirectHitPrice(hit, direction),
        }))
        .sort((left, right) => {
            const diff = comparePriceSortValues(
                left.comparablePrice,
                right.comparablePrice,
                direction
            );

            if (diff !== 0) {
                return diff;
            }

            const directDiff = comparePriceSortValues(
                left.directPrice,
                right.directPrice,
                direction
            );

            if (directDiff !== 0) {
                return directDiff;
            }

            return left.originalIndex - right.originalIndex;
        })
        .map(({ hit }) => hit);
};

const buildPlpGroupsByKey = (groups = []) => {
    const groupsByKey = new Map();

    groups.forEach((group) => {
        if (!group?.xGroupKey) {
            return;
        }

        groupsByKey.set(group.xGroupKey, Array.isArray(group.hits) ? group.hits : []);
    });

    return groupsByKey;
};

const setCachedPlpGroupsByKey = (groups = []) => {
    const groupsByKey = buildPlpGroupsByKey(groups);
    window.__poLastPLPGroupsByKey = groupsByKey;
    return groupsByKey;
};

const getCachedPlpGroupHits = (groupKey) => {
    if (!groupKey) {
        return null;
    }

    const sharedGroups = window.__poLastPLPGroupsByKey;
    if (sharedGroups instanceof Map && sharedGroups.has(groupKey)) {
        return sharedGroups.get(groupKey) || null;
    }

    try {
        const raw = sessionStorage.getItem("lastPLPGroups") || "[]";

        if (raw !== lastPlpGroupsRaw || !(window.__poLastPLPGroupsByKey instanceof Map)) {
            const parsed = JSON.parse(raw);
            const groups = Array.isArray(parsed) ? parsed : [];
            lastPlpGroupsRaw = raw;
            setCachedPlpGroupsByKey(groups);
        }
    } catch (_error) {
        window.__poLastPLPGroupsByKey = new Map();
    }

    return window.__poLastPLPGroupsByKey instanceof Map
        ? window.__poLastPLPGroupsByKey.get(groupKey) || null
        : null;
};

const getCachedPlpFullHit = (handle) => {
    if (!handle) {
        return null;
    }

    return window.__poLastPLPFullHitsByHandle instanceof Map
        ? window.__poLastPLPFullHitsByHandle.get(handle) || null
        : null;
};

const getCachedProductInfo = (productUrl) => {
    if (!productInfoCache.has(productUrl)) {
        return null;
    }

    const cached = productInfoCache.get(productUrl);
    productInfoCache.delete(productUrl);
    productInfoCache.set(productUrl, cached);
    return cached;
};

const setCachedProductInfo = (productUrl, data) => {
    if (!productUrl || !data) {
        return data;
    }

    if (productInfoCache.has(productUrl)) {
        productInfoCache.delete(productUrl);
    }

    productInfoCache.set(productUrl, data);

    while (productInfoCache.size > PRODUCT_INFO_CACHE_LIMIT) {
        const oldestKey = productInfoCache.keys().next().value;
        if (oldestKey === undefined) break;
        productInfoCache.delete(oldestKey);
    }

    return data;
};

const createTemplateContent = (templateHTML) => {
    if (!templateHTML) {
        return null;
    }

    const template = document.createElement("template");
    template.innerHTML = templateHTML;
    return template.content.cloneNode(true);
};

let plpProductCardHydrationObserver = null;

const getPlpProductCardHydrationObserver = () => {
    if (plpProductCardHydrationObserver || typeof IntersectionObserver !== "function") {
        return plpProductCardHydrationObserver;
    }

    plpProductCardHydrationObserver = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) {
                    return;
                }

                plpProductCardHydrationObserver.unobserve(entry.target);
                entry.target?.hydrateCard?.();
            });
        },
        { rootMargin: "600px 0px" }
    );

    return plpProductCardHydrationObserver;
};

!customElements.get("po-plp-product-card") && customElements.define("po-plp-product-card", class extends PoAlgoliaUtilities {
    connectedCallback () {
        this.groupKey = this.dataset.groupKey;
        this.handle = this.dataset.pdHandle;
        this.productId = this.dataset.pdId;
        this.hits = [];
        this.visibleHits = [];
        const card = this.closest(".po-algolia-product-card");
        this.productAnchor = card?.querySelector("[data-product-href], .tile-title a");
        this.template = this.querySelector("template");
        this.templateName = this.closest("[data-template-name]")?.dataset.templateName;
        this.ungrouped = this.checkUngrouped();
        this.toggleAttribute("data-hide-variant-carousel", this.ungrouped);
        this.shopRoot = window.Shopify?.routes?.root || "/";
        this.sliderComponent = null;
        this.currentPage = 1;
        this.currentProductUrl = `${this.shopRoot}products/${this.handle}`;
        this.currentVariantHandle = this.handle || "";
        this.collectionHandle = window.collection?.handle || "";
        this.plpHandle = window.collection?.plp_handle || window.collection?.handle || "all-products";
        this.cardWrapper = this.closest(".tw-po-tile[data-card-ready]");
        this.pendingProductInfo = new Map();
        this.productInfoControllers = new Map();
        this.renderToken = 0;
        this.isHydrated = false;
        this.hoverIntentHandler = () => this.hydrateHoverImagesForCurrentSlide();

        poPlpCardSortDebug("connected", {
            handle: this.handle,
            groupKey: this.groupKey || null,
            currentIndexName: getCurrentPlpIndexName(),
        });

        this.plpInfoRequestId = 0;
        this.observeHydration();
    }

    getCurrentPriceSortDirection () {
        return getPriceSortDirection(getCurrentPlpIndexName());
    }

    getSortedPriceHits () {
        const direction = this.getCurrentPriceSortDirection();
        const cachedGroupHits = this.groupKey ? getCachedPlpGroupHits(this.groupKey) : null;
        const sourceHits = Array.isArray(cachedGroupHits) && cachedGroupHits.length > 0
            ? cachedGroupHits
            : Array.isArray(this.visibleHits) && this.visibleHits.length > 0
                ? this.visibleHits
                : Array.isArray(this.hits) && this.hits.length > 0
                    ? this.hits
                    : [];

        return sortHitsForPriceDisplay(sourceHits, direction);
    }

    disconnectedCallback () {
        getPlpProductCardHydrationObserver()?.unobserve(this);
        this.abortPendingProductInfoRequests();
        this.removeSliderComponent();
        this.removeEventListener("mouseenter", this.hoverIntentHandler);
        document.removeEventListener("plp:groups-loaded", this.handleGroupsLoaded);
    }

    abortPendingProductInfoRequests () {
        this.productInfoControllers.forEach((controller) => controller.abort());
        this.productInfoControllers.clear();
        this.pendingProductInfo.clear();
    }

    startRender () {
        this.renderToken += 1;
        return this.renderToken;
    }

    checkUngrouped () {
        const collectionNotGrouped = window.collection?.not_grouped;
        return this.templateName === "search"
            || this.closest("po-algolia-header-search")
            || collectionNotGrouped === true
            || collectionNotGrouped === "true"
            || collectionNotGrouped === 1
            || collectionNotGrouped === "1";
    }

    shouldHydrateImmediately () {
        if (this.shouldPreserveAlgoliaGroupOrder()) {
            return true;
        }

        const listItemIndex = this.getListItemIndex();
        return listItemIndex > -1 && listItemIndex < 8;
    }

    observeHydration () {
        if (this.shouldHydrateImmediately()) {
            this.hydrateCard();
            return;
        }

        const observer = getPlpProductCardHydrationObserver();
        if (!observer) {
            this.hydrateCard();
            return;
        }

        observer.observe(this);
    }

    hydrateCard () {
        if (this.isHydrated) {
            return;
        }

        this.isHydrated = true;
        this.addEventListener("mouseenter", this.hoverIntentHandler);
        document.addEventListener("plp:groups-loaded", this.handleGroupsLoaded);

        if (this.ungrouped) {
            this.renderStaticThumb();
        } else if (this.groupKey) {
            this.loadProductGroup();
        } else {
            this.renderStaticThumb();
        }
    }

    getInitialFeaturedImage () {
        return this.dataset.featuredImage || null;
    }

    getInitialHoverImage () {
        return this.dataset.hoverImage || null;
    }

    getInitialProductTitle () {
        return this.dataset.productTitle || "";
    }

    setSliderTotalCount (root, count) {
        root?.querySelectorAll(".slider-counter--total, [data-slide-count]").forEach((element) => {
            element.textContent = count;
        });
    }

    markCardReady () {
        if (this.cardWrapper) {
            this.cardWrapper.dataset.cardReady = "true";
        }
    }

    watchMainImageReady (img, shouldWatch) {
        const mediaWrapper = img?.closest?.(".po-card-image[data-image-ready]");
        const markReady = () => {
            if (mediaWrapper) {
                mediaWrapper.dataset.imageReady = "true";
            }
            this.markCardReady();
        };

        if (!shouldWatch || !this.cardWrapper) {
            return;
        }

        if (!img) {
            markReady();
            return;
        }

        if (img.complete && img.naturalWidth > 0) {
            markReady();
            return;
        }

        img.addEventListener("load", markReady, { once: true });
        img.addEventListener("error", markReady, { once: true });
    }

    setProductLinks (root, productUrl, shouldPersistSession = false) {
        root.querySelectorAll("[data-pd-url]").forEach((element) => {
            element.href = productUrl;
            element.dataset.pdUrl = productUrl;

            if (shouldPersistSession) {
                element.addEventListener("click", () => this.saveSessionHits());
            }
        });
    }

    upsertImage (wrapper, imageUrl, { alt, title, width, height, lazyLoad }) {
        if (!wrapper || !imageUrl) {
            return null;
        }

        const img = wrapper.querySelector("img") || document.createElement("img");
        const isSearchMode = this.dataset.searchMode === "true";
        const shouldPreserveOriginalMediaAspect = true;
        const nextSrc = this.formatImageUrl(
            imageUrl,
            width,
            shouldPreserveOriginalMediaAspect ? null : height
        );

        if (img.src !== nextSrc) {
            img.src = nextSrc;
        }

        img.alt = alt || "";
        img.title = title || alt || "";
        if (width) {
            img.width = width;
        } else {
            img.removeAttribute("width");
        }

        if (height) {
            img.height = height;
        } else {
            img.removeAttribute("height");
        }

        if (isSearchMode) {
            wrapper.style.display = "block";
            wrapper.style.width = "100%";
            wrapper.style.height = "100%";
            wrapper.style.padding = "0";

            img.className = "tw-w-full tw-h-full tw-object-contain tw-object-center";
            img.style.width = "100%";
            img.style.height = "100%";
            img.style.maxWidth = "none";
            img.style.maxHeight = "none";
            img.style.margin = "0";
            img.style.objectFit = "contain";
            img.style.objectPosition = this.isDesktop() ? "center 58%" : "center 54%";
            img.style.display = "block";
            img.style.transform = "none";
        } else {
            wrapper.style.removeProperty("display");
            wrapper.style.removeProperty("width");
            wrapper.style.removeProperty("height");
            wrapper.style.removeProperty("padding");

            img.className = "tw-w-full tw-h-full tw-object-contain tw-object-center";
            img.style.removeProperty("width");
            img.style.removeProperty("height");
            img.style.removeProperty("max-width");
            img.style.removeProperty("max-height");
            img.style.removeProperty("margin");
            img.style.removeProperty("object-fit");
            img.style.removeProperty("object-position");
            img.style.removeProperty("display");
            img.style.removeProperty("transform");
        }

        if (lazyLoad) {
            img.loading = "lazy";
        } else {
            img.removeAttribute("loading");
        }

        if (!img.isConnected) {
            wrapper.appendChild(img);
        }

        return img;
    }

    renderSlideMedia (
        slide,
        {
            featuredImage,
            hoverImage,
            mainAlt,
            mainTitle,
            hoverAlt,
            hoverTitle,
            lazyLoad,
            shouldWatchMain = false,
            eagerHover = false,
        }
    ) {
        const imgWidth = this.isDesktop() ? PLP_IMAGE_WIDTH_DESKTOP : PLP_IMAGE_WIDTH_MOBILE;
        const hasHoverImage = Boolean(hoverImage && hoverImage !== featuredImage);
        const mainWrappers = slide.querySelectorAll("[data-pd-image-wrapper]");
        const hoverWrappers = slide.querySelectorAll("[data-pd-image-hover-wrapper]");

        if (!featuredImage) {
            if (shouldWatchMain) {
                this.markCardReady();
            }
            return;
        }

        mainWrappers.forEach((wrapper) => {
            wrapper.classList.toggle("sd:group-hover:tw-opacity-0", hasHoverImage);
            const img = this.upsertImage(wrapper, featuredImage, {
                alt: mainAlt,
                title: mainTitle,
                width: imgWidth,
                height: null,
                lazyLoad,
            });

            this.watchMainImageReady(img, shouldWatchMain);
        });

        hoverWrappers.forEach((wrapper) => {
            wrapper.classList.toggle("tw-pointer-events-none", !hasHoverImage);

            if (!hasHoverImage) {
                delete wrapper.dataset.hoverSrc;
                delete wrapper.dataset.hoverAlt;
                delete wrapper.dataset.hoverTitle;
                wrapper.removeAttribute("href");
                wrapper.setAttribute("aria-hidden", "true");
                wrapper.tabIndex = -1;
                wrapper.innerHTML = "";
                return;
            }

            wrapper.dataset.hoverSrc = hoverImage;
            wrapper.dataset.hoverAlt = hoverAlt || "";
            wrapper.dataset.hoverTitle = hoverTitle || hoverAlt || "";
            wrapper.removeAttribute("aria-hidden");
            wrapper.removeAttribute("tabindex");
            if (wrapper.dataset.pdUrl) {
                wrapper.href = wrapper.dataset.pdUrl;
            }

            if (eagerHover || wrapper.querySelector("img")) {
                this.upsertImage(wrapper, hoverImage, {
                    alt: hoverAlt || "",
                    title: hoverTitle || hoverAlt || "",
                    width: imgWidth,
                    height: null,
                    lazyLoad,
                });
            }
        });
    }

    hydrateHoverImages (slide) {
        if (!slide || !this.isDesktop()) {
            return;
        }

        const imgWidth = this.isDesktop() ? PLP_IMAGE_WIDTH_DESKTOP : PLP_IMAGE_WIDTH_MOBILE;

        slide.querySelectorAll("[data-pd-image-hover-wrapper]").forEach((wrapper) => {
            const hoverSrc = wrapper.dataset.hoverSrc;
            if (!hoverSrc || wrapper.querySelector("img")) {
                return;
            }

            this.upsertImage(wrapper, hoverSrc, {
                alt: wrapper.dataset.hoverAlt || "",
                title: wrapper.dataset.hoverTitle || wrapper.dataset.hoverAlt || "",
                width: imgWidth,
                height: null,
                lazyLoad: true,
            });
        });
    }

    hydrateHoverImagesForCurrentSlide () {
        const currentSlide = this.currentPage > 0
            ? this.getSlideByProductUrl(this.getProductUrlByPage(this.currentPage))
            : this.querySelector("[data-product-url]");

        this.hydrateHoverImages(currentSlide);
    }

    createProductSlide (hit, index, slideTemplate, productUrl, cardIndex) {
        const slide = slideTemplate.cloneNode(true);
        const lazyLoad = cardIndex >= 3 || index > 0;
        const isFirstSlide = index === 0;
        const isBaseProduct = hit.handle === this.handle;
        const collectionHandles = Array.isArray(hit?.collections)
            ? hit.collections.map((value) => normalizeCollectionHandle(value)).filter(Boolean)
            : [];
        const initialFeaturedImage = isBaseProduct ? this.getInitialFeaturedImage() : null;
        const initialHoverImage = isBaseProduct ? this.getInitialHoverImage() : null;
        const initialTitle = isBaseProduct ? this.getInitialProductTitle() : "";
        const featuredImage = initialFeaturedImage || hit.image;
        const hoverImage = initialHoverImage || null;
        const slideTitle = initialTitle || hit.title;

        slide.id = `${slideTemplate.id}-${index + 1}`;
        slide.dataset.pdSlide = hit.objectID;
        slide.dataset.productUrl = productUrl;
        slide.dataset.productHandle = hit.handle || "";
        slide.dataset.variantHandle = hit.handle || "";
        slide.dataset.variantCollectionHandles = collectionHandles.join(",") || "[[--NONE--]]";
        slide.dataset.poHydrationState = "idle";

        this.setProductLinks(slide, productUrl, true);
        this.renderSlideMedia(slide, {
            featuredImage,
            hoverImage,
            mainAlt: slideTitle,
            mainTitle: slideTitle,
            hoverAlt: slideTitle,
            hoverTitle: slideTitle,
            lazyLoad,
            shouldWatchMain: isFirstSlide,
            eagerHover: isFirstSlide && Boolean(hoverImage),
        });

        return slide;
    }

    createProductThumbnail (hit, index, thumbTemplate, productUrl) {
        const thumb = thumbTemplate.cloneNode(true);
        const thumbImg = thumb.querySelector("img") || document.createElement("img");
        const thumbLabel = thumb.querySelector("[data-thumb-label]");
        const thumbBadge = thumb.querySelector("[data-thumb-badge]");
        const collectionHandles = Array.isArray(hit?.collections)
            ? hit.collections.map((value) => normalizeCollectionHandle(value)).filter(Boolean)
            : [];
        const variantLabel = hit?.meta?.custom?.child_badge?.value
            || hit?.meta?.custom?.child_badge
            || hit?.title
            || "";

        thumb.id = `${thumbTemplate.id}-${index + 1}`;
        thumb.dataset.selected = index === 0;
        thumb.dataset.href = productUrl;
        thumb.dataset.pdThumb = hit.objectID || hit.id || hit.handle || `${index + 1}`;
        thumb.dataset.index = index;
        thumb.dataset.currentVariant = hit.handle || "";
        thumb.dataset.variantHandle = hit.handle || "";
        thumb.dataset.variantCollectionHandles = collectionHandles.join(",") || "[[--NONE--]]";
        thumb.dataset.plpStatus = "";

        if (thumbLabel) {
            thumbLabel.textContent = variantLabel;
        }

        if (thumbBadge) {
            thumbBadge.innerHTML = "";
        }

        if (hit.image) {
            thumbImg.src = this.formatImageUrl(hit.image, PLP_THUMB_WIDTH, PLP_THUMB_HEIGHT);
            thumbImg.alt = hit.title || "";
            thumbImg.loading = "lazy";
            thumbImg.width = PLP_THUMB_WIDTH;
            thumbImg.height = PLP_THUMB_HEIGHT;
            thumbImg.className = "variant-image tw-select-none tw-pointer-events-none";

            if (!thumbImg.isConnected) {
                thumb.appendChild(thumbImg);
            }
        }

        return thumb;
    }

    formatImageUrl (url, width, height) {
        const parsedUrl = url ? new URL(url, window.location.origin) : null;

        if (!parsedUrl) {
            console.error("Invalid URL:", url);
            return url;
        }

        if (width) {
            parsedUrl.searchParams.set("width", width);
        }

        if (height) {
            parsedUrl.searchParams.set("height", height);
        } else {
            parsedUrl.searchParams.delete("height");
        }

        return parsedUrl.toString();
    }

    getListItemIndex () {
        const li = this.closest("li");
        return li ? Array.prototype.indexOf.call(li.parentNode.children, li) : -1;
    }

    shouldPreserveAlgoliaGroupOrder () {
        return isPriceSortIndexName(getCurrentPlpIndexName());
    }

    async getProductInfo (productUrl) {
        const cached = getCachedProductInfo(productUrl);
        if (cached) {
            return cached;
        }

        if (this.pendingProductInfo.has(productUrl)) {
            return this.pendingProductInfo.get(productUrl);
        }

        const controller = new AbortController();
        this.productInfoControllers.set(productUrl, controller);

        const request = fetch(`${productUrl}?view=po-info-badge`, { signal: controller.signal })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                return response.text();
            })
            .then((text) => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(text, "text/html");
                const jsonData = doc.querySelector('script[type="application/json"]')?.textContent?.trim();

                return setCachedProductInfo(productUrl, {
                    product: jsonData ? JSON.parse(jsonData) : null,
                    templateHTML: doc.querySelector("template")?.innerHTML || "",
                });
            })
            .catch((error) => {
                if (error?.name !== "AbortError") {
                    console.error("Error fetching product info:", error);
                }

                return null;
            })
            .finally(() => {
                this.productInfoControllers.delete(productUrl);
                this.pendingProductInfo.delete(productUrl);
            });

        this.pendingProductInfo.set(productUrl, request);
        return request;
    }

    getProductUrlByPage (pageNumber) {
        if (!this.sliderComponent) {
            return null;
        }

        const slides = this.getActiveSlides();
        const thumbnails = this.getActiveThumbnails();
        const slide = slides[pageNumber - 1];
        const thumbnailUrl = thumbnails[pageNumber - 1]?.dataset?.href;

        return slide?.dataset?.productUrl || thumbnailUrl || null;
    }

    getActiveSlides () {
        if (!this.sliderComponent) {
            return [];
        }

        const sliderItemsToShow = this.sliderComponent.sliderItemsToShow;
        if (sliderItemsToShow?.length) {
            return Array.from(sliderItemsToShow);
        }

        const sliderItems = this.sliderComponent.sliderItems;
        if (sliderItems?.length) {
            return Array.from(sliderItems);
        }

        return Array.from(
            this.sliderComponent.querySelectorAll("[data-product-url]:not([data-po-hidden])")
        );
    }

    getActiveThumbnails () {
        if (!this.sliderComponent) {
            return [];
        }

        const thumbnailItems = this.sliderComponent.thumbnailItems;
        if (thumbnailItems?.length) {
            return Array.from(thumbnailItems);
        }

        return Array.from(
            this.sliderComponent.querySelectorAll("[data-current-variant]:not([data-po-hidden])")
        );
    }

    findHitByHandle (handle) {
        if (!handle) {
            return null;
        }

        return this.hits.find((hit) => hit?.handle === handle)
            || this.visibleHits.find((hit) => hit?.handle === handle)
            || null;
    }

    getHandleByPage (pageNumber) {
        const slides = this.getActiveSlides();
        const thumbnails = this.getActiveThumbnails();
        const slide = slides[pageNumber - 1];
        const thumb = thumbnails[pageNumber - 1];

        return thumb?.dataset?.currentVariant || slide?.dataset?.variantHandle || null;
    }

    getVisibleHitByPage (pageNumber) {
        const handle = this.getHandleByPage(pageNumber);
        return this.findHitByHandle(handle) || this.visibleHits?.[pageNumber - 1] || null;
    }

    getCollectionScopedHits (hits = []) {
        const normalizedHits = Array.isArray(hits) ? hits : [];
        const currentCollectionHandle = normalizeCollectionHandle(this.collectionHandle);
        const plpCollectionHandle = normalizeCollectionHandle(this.plpHandle);
        const isScopedCollectionPage = Boolean(
            currentCollectionHandle
            && plpCollectionHandle
            && currentCollectionHandle !== plpCollectionHandle
        );
        const candidateHandles = [
            currentCollectionHandle,
            isScopedCollectionPage ? "" : plpCollectionHandle,
        ]
            .filter((value, index, items) => value && items.indexOf(value) === index);

        for (const candidateHandle of candidateHandles) {
            const scopedHits = normalizedHits.filter((hit) => {
                const collections = Array.isArray(hit?.collections)
                    ? hit.collections.map((value) => normalizeCollectionHandle(value))
                    : [];
                return collections.includes(candidateHandle);
            });

            if (scopedHits.length > 0) {
                return {
                    handle: candidateHandle,
                    hits: scopedHits,
                };
            }
        }

        return {
            handle: candidateHandles[0] || "",
            hits: [],
        };
    }

    getSlideByProductUrl (productUrl) {
        if (!productUrl) {
            return null;
        }

        return Array.from(this.querySelectorAll("[data-product-url]")).find(
            (slide) => slide.dataset.productUrl === productUrl
        ) || null;
    }

    ensureSlideHydrated (slide, hit, productUrl, options = {}) {
        if (!slide || !productUrl) {
            return Promise.resolve(null);
        }

        if (slide.dataset.poHydrationState === "loaded") {
            return Promise.resolve(getCachedProductInfo(productUrl));
        }

        const lazyLoad = options.lazyLoad ?? true;
        const shouldWatchMain = options.shouldWatchMain === true;
        const eagerHover = options.eagerHover === true;

        slide.dataset.poHydrationState = "loading";

        return this.getProductInfo(productUrl).then((data) => {
            if (!this.isConnected || !slide.isConnected) {
                return data;
            }

            const product = data?.product;
            const featuredImage = product?.featured_image || hit?.image;
            const hasHoverImage = product?.images?.length > 1;
            const hoverImage = hasHoverImage ? product.images[1] : null;
            const fallbackTitle = product?.title || hit?.title || "";

            if (this.dataset.nontransactional !== "true" && product) {
                slide.toggleAttribute("data-unavailable", !product?.available);
            }

            this.renderSlideMedia(slide, {
                featuredImage,
                hoverImage,
                mainAlt: product?.alt_image || fallbackTitle,
                mainTitle: product?.title_image || fallbackTitle,
                hoverAlt: product?.alt_hover || fallbackTitle,
                hoverTitle: product?.title_hover || fallbackTitle,
                lazyLoad,
                shouldWatchMain,
                eagerHover,
            });

            slide.dataset.poHydrationState = data ? "loaded" : "error";

            if (!featuredImage && shouldWatchMain) {
                this.markCardReady();
            }

            return data;
        });
    }

    handleGroupsLoaded = (event) => {
        const groups = event?.detail || event?.details;

        if (Array.isArray(groups)) {
            setCachedPlpGroupsByKey(groups);
        }

        if (Array.isArray(groups) && groups.some((group) => group.xGroupKey === this.groupKey)) {
            this.loadProductGroup();
        }
    }

    isDesktop () {
        return window.matchMedia("(min-width: 1024px)").matches;
    }

    loadProductGroup () {
        this.abortPendingProductInfoRequests();
        this.hits = [];

        const hits = filterNonRxHits(getCachedPlpGroupHits(this.groupKey) || []);

        poPlpCardSortDebug("loadProductGroup", {
            handle: this.handle,
            groupKey: this.groupKey || null,
            currentIndexName: getCurrentPlpIndexName(),
            cachedHits: Array.isArray(hits) ? hits.map((hit) => hit?.handle || null) : [],
        });

        if (Array.isArray(hits) && hits.length > 0) {
            this.hits = hits.slice();
            this.removeSliderComponent();
            this.renderThumb();
        }

        if (this.hits.length === 0) {
            this.removeSliderComponent();
            this.renderStaticThumb();
        }
    }

    onProductSwipe (productUrl, requestId) {
        if (this.productAnchor && productUrl) {
            this.productAnchor.href = productUrl;
        }

        this.getProductInfo(productUrl).then((data) => {
            if (!this.isConnected || requestId !== this.plpInfoRequestId) {
                return;
            }

            if (productUrl !== this.currentProductUrl) {
                return;
            }

            const content = createTemplateContent(data?.templateHTML);
            const currentSlide = this.getSlideByProductUrl(productUrl);
            this.currentVariantHandle = currentSlide?.dataset?.variantHandle || this.currentVariantHandle;

            if (content) {
                this.updateBadge(content);
                this.updateDNPrice(content);
                this.updateCaption(content);
            }

            if (currentSlide?.matches(":hover")) {
                this.hydrateHoverImages(currentSlide);
            }
        });
    }

    /**
     * PLP card price is rendered from Liquid (most_popular_variant, same as PDP).
     * Do not overwrite with Algolia group min/max — Oliver Peoples updates price via fetched product card only.
     * @returns {{ priceDirection: string|null, comparablePrice: null, displayPrice: null }}
     */
    applySortedDisplayPrice () {
        return {
            priceDirection: this.getCurrentPriceSortDirection(),
            comparablePrice: null,
            displayPrice: null,
        };
    }

    syncInitialVisibleProduct (hit, cardIndex) {
        const productHandle = hit?.handle || this.handle;
        if (!productHandle) {
            return;
        }

        const productUrl = `${this.shopRoot}products/${productHandle}`;
        const slide = this.getActiveSlides()[0] || this.getSlideByProductUrl(productUrl);

        this.currentPage = 1;
        this.currentProductUrl = productUrl;
        this.currentVariantHandle = productHandle;

        if (this.productAnchor) {
            this.productAnchor.href = productUrl;
        }

        if (productHandle === this.handle) {
            this.applySortedDisplayPrice(
                this.closest(".po-algolia-product-card"),
                this.closest(".tw-po-tile")
            );
            return;
        }

        this.ensureSlideHydrated(slide, hit, productUrl, {
            lazyLoad: cardIndex >= 3,
            shouldWatchMain: false,
            eagerHover: true,
        }).finally(() => {
            if (this.isConnected) {
                const requestId = ++this.plpInfoRequestId;
                this.onProductSwipe(productUrl, requestId);

                if (this.matches(":hover") && slide) {
                    this.hydrateHoverImages(slide);
                }
            }
        });
    }

    renderStaticThumb () {
        const clone = this.template?.content?.cloneNode(true);
        const sliderComponent = clone?.querySelector("po-slider-component");
        const renderToken = this.startRender();

        if (!sliderComponent) {
            console.error("Slider component not found in template.");
            return;
        }

        this.currentPage = 1;
        this.visibleHits = [
            {
                handle: this.handle,
                objectID: this.productId,
                title: "",
                image: null,
                collections: [this.plpHandle],
            },
        ];

        const slider = sliderComponent.querySelector("[data-slider]");
        const slide = sliderComponent.querySelector("[data-pd-slide]");
        const slideClone = slide.cloneNode(true);
        const thumb = sliderComponent.querySelector("[data-pd-thumb]");
        const thumbContainer = sliderComponent.querySelector("[data-thumbnails]");
        const controllers = Array.from(sliderComponent.querySelectorAll("[data-controller]"));
        const productUrl = `${this.shopRoot}products/${this.handle}`;
        const lazyLoad = this.getListItemIndex() >= 3;
        const featuredImage = this.getInitialFeaturedImage();
        const hoverImage = this.getInitialHoverImage();
        const productTitle = this.getInitialProductTitle();

        slideClone.id = `${slide.id}-1`;
        slideClone.dataset.pdSlide = this.productId;
        slideClone.dataset.productUrl = productUrl;
        slideClone.dataset.productHandle = this.handle || "";
        slideClone.dataset.variantHandle = this.handle || "";
        slideClone.dataset.poHydrationState = "loaded";

        this.setProductLinks(slideClone, productUrl);

        if (thumbContainer) {
            controllers.forEach((controller) => {
                if (this.ungrouped) {
                    controller.style.display = "none";
                } else {
                    controller.style.visibility = "hidden";
                }
            });
            thumbContainer.style.visibility = "hidden";
        }

        if (!this.isConnected || renderToken !== this.renderToken) {
            return;
        }

        this.renderSlideMedia(slideClone, {
            featuredImage,
            hoverImage,
            mainAlt: productTitle,
            mainTitle: productTitle,
            hoverAlt: productTitle,
            hoverTitle: productTitle,
            lazyLoad,
            shouldWatchMain: true,
            eagerHover: Boolean(hoverImage),
        });

        if (!featuredImage) {
            this.markCardReady();
        }

        if (!this.ungrouped) {
            const thumbClone = this.createProductThumbnail(
                {
                    image: featuredImage,
                    title: productTitle,
                    objectID: this.productId,
                    handle: this.handle,
                },
                0,
                thumb,
                productUrl
            );
            thumbContainer.appendChild(thumbClone);
        }

        this.setSliderTotalCount(sliderComponent, 1);

        slider.appendChild(slideClone);
        slide.remove();
        thumb?.remove();

        if (!this.querySelector("po-slider-component")) {
            this.appendChild(sliderComponent);
        }

        this.sliderComponent = sliderComponent;
        this.applySortedDisplayPrice(
            this.closest(".po-algolia-product-card"),
            this.closest(".tw-po-tile")
        );
    }

    renderThumb () {
        const clone = this.template?.content?.cloneNode(true);
        const sliderComponent = clone?.querySelector("po-slider-component");

        if (!sliderComponent) {
            console.error("Slider component not found in template.");
            return;
        }

        this.startRender();
        const slider = sliderComponent.querySelector("[data-slider]");
        const slide = sliderComponent.querySelector("[data-pd-slide]");
        const thumbContainer = sliderComponent.querySelector("[data-thumbnails]");
        const thumb = sliderComponent.querySelector("[data-pd-thumb]");
        const controllers = Array.from(sliderComponent.querySelectorAll("[data-controller]"));
        const cardIndex = this.getListItemIndex();

        this.currentPage = 1;

        const { handle: resolvedCollectionHandle, hits: scopedCollectionHits } = this.getCollectionScopedHits(this.hits);
        const currentCollectionHandle = normalizeCollectionHandle(this.collectionHandle);
        const plpCollectionHandle = normalizeCollectionHandle(this.plpHandle);
        const isScopedCollectionPage = Boolean(
            currentCollectionHandle
            && plpCollectionHandle
            && currentCollectionHandle !== plpCollectionHandle
        );
        let collectionHits = scopedCollectionHits;

        if (collectionHits.length === 0) {
            if (isScopedCollectionPage) {
                const currentProductHit = this.hits.find((hit) => hit?.handle === this.handle) || null;
                collectionHits = currentProductHit ? [currentProductHit] : [];
                console.warn(`No scoped hits found for collection handle: ${currentCollectionHandle}. Falling back to current product only.`);
            } else {
                collectionHits = this.hits;
                console.warn(`No hits found for collection handles: ${[this.collectionHandle, this.plpHandle].filter(Boolean).join(", ")}. Displaying all hits.`);
            }
        }

        const collectionHitsBeforeRender = collectionHits.map((hit) => hit?.handle || null);

        poPlpCardSortDebug("renderThumb", {
            handle: this.handle,
            groupKey: this.groupKey || null,
            currentIndexName: getCurrentPlpIndexName(),
            collectionHandle: this.collectionHandle || null,
            plpHandle: this.plpHandle || null,
            resolvedCollectionHandle: resolvedCollectionHandle || null,
            allGroupHits: this.hits.map((hit) => hit?.handle || null),
            collectionHitsBeforeRender,
            collectionHitsAfterRender: collectionHits.map((hit) => hit?.handle || null),
        });

        this.visibleHits = collectionHits;

        if (collectionHits.length === 0) {
            this.markCardReady();
        }

        collectionHits.forEach((hit, index) => {
            const productUrl = `${this.shopRoot}products/${hit.handle}`;
            const productSlide = this.createProductSlide(hit, index, slide, productUrl, cardIndex);
            const thumbClone = this.createProductThumbnail(hit, index, thumb, productUrl);

            slider.appendChild(productSlide);
            thumbContainer.appendChild(thumbClone);
        });

        if (collectionHits.length === 1) {
            controllers.forEach((controller) => {
                controller.style.visibility = "hidden";
            });
            thumbContainer.style.visibility = "hidden";
        } else if (collectionHits.length <= 4) {
            thumbContainer.style.justifyContent = "center";
        }

        this.setSliderTotalCount(sliderComponent, collectionHits.length);

        slide.remove();
        thumb.remove();
        this.appendChild(sliderComponent);

        sliderComponent.addEventListener("slideChanged", this.slideChangedHandler);
        this.sliderComponent = sliderComponent;

        if (collectionHits.length > 0) {
            this.syncInitialVisibleProduct(collectionHits[0], cardIndex);
            const cardRoot = this.closest(".po-algolia-product-card");
            const firstHit = collectionHits[0];
            const firstProductUrl = `${this.shopRoot}products/${firstHit.handle}`;
            const PoSlider = window.PoSliderComponent;

            this.getProductInfo(firstProductUrl).then((data) => {
                if (!this.isConnected) {
                    return;
                }

                const content = createTemplateContent(data?.templateHTML);
                const statusText = content?.querySelector("[data-prod-status]")?.textContent?.trim() ?? "";

                if (PoSlider?.applyPlpProdStatus) {
                    PoSlider.applyPlpProdStatus(cardRoot, statusText);
                }

                const firstThumb = this.getActiveThumbnails()[0];
                if (firstThumb) {
                    firstThumb.dataset.plpStatus = statusText;
                }
            });
        }
    }

    removeSliderComponent () {
        if (this.sliderComponent) {
            this.sliderComponent.removeEventListener("slideChanged", this.slideChangedHandler);
            this.sliderComponent.remove();
            this.sliderComponent = null;
        }
    }

    saveSessionHits () {
        sessionStorage.setItem("lastGroupItem", JSON.stringify({
            xGroupKey: this.groupKey,
            hits: this.hits,
        }));
    }

    slideChangedHandler = (event) => {
        const pageNumber = event.detail?.currentPage || 1;
        const currentSlide = event.detail?.currentElement || this.getActiveSlides()[pageNumber - 1] || null;
        const currentThumbnail = event.detail?.currentThumbnail || this.getActiveThumbnails()[pageNumber - 1] || null;
        const currentHandle = event.detail?.currentHandle
            || currentThumbnail?.dataset?.currentVariant
            || currentSlide?.dataset?.variantHandle
            || null;
        const productUrl = currentSlide?.dataset?.productUrl
            || currentThumbnail?.dataset?.href
            || this.getProductUrlByPage(pageNumber);

        if (
            pageNumber === this.currentPage
            && productUrl === this.currentProductUrl
            && currentHandle === this.currentVariantHandle
        ) {
            return;
        }

        this.currentPage = pageNumber;
        if (!productUrl) {
            return;
        }

        this.currentProductUrl = productUrl;
        this.currentVariantHandle = currentHandle || this.currentVariantHandle;

        if (this.productAnchor) {
            this.productAnchor.href = productUrl;
        }

        const card = this.closest(".po-algolia-product-card");
        const thumbStatus = currentThumbnail?.dataset?.plpStatus?.trim() ?? "";
        const PoSlider = window.PoSliderComponent;
        if (PoSlider?.applyPlpProdStatus) {
            PoSlider.applyPlpProdStatus(card, thumbStatus);
        }

        const requestId = ++this.plpInfoRequestId;

        const slide = currentSlide || this.getSlideByProductUrl(productUrl);
        const hit = this.findHitByHandle(currentHandle) || this.getVisibleHitByPage(pageNumber);

        poPlpCardSortDebug("slideChanged", {
            handle: this.handle,
            groupKey: this.groupKey || null,
            pageNumber,
            currentHandle,
            productUrl,
            hitHandle: hit?.handle || null,
        });

        this.ensureSlideHydrated(slide, hit, productUrl, {
            lazyLoad: true,
            shouldWatchMain: false,
            eagerHover: true,
        }).finally(() => {
            if (this.isConnected && requestId === this.plpInfoRequestId) {
                this.onProductSwipe(productUrl, requestId);
                if (this.matches(":hover")) {
                    this.hydrateHoverImages(slide);
                }
            }
        });
    }

    updateBadge (templateContent) {
        const badgeWrapper = this.querySelector("[data-badge-wrapper]");

        if (badgeWrapper && templateContent) {
            badgeWrapper.replaceWith(templateContent.querySelector("[data-badge-wrapper]") || badgeWrapper);
        }
    }

    updateDNPrice (templateContent) {
        const card = this.closest(".po-algolia-product-card");
        const tile = this.closest(".tw-po-tile");
        const dnWrapper = templateContent?.querySelector("[data-dn-wrapper]");
        const priceEl = tile?.querySelector(".dinamic-price")
            || card?.querySelector(".dinamic-price")
            || tile?.querySelector("[data-la-dn-price]")
            || card?.querySelector("[data-la-dn-price]");
        const newPriceEl = templateContent?.querySelector(".dinamic-price")
            || dnWrapper?.querySelector(".dinamic-price")
            || dnWrapper?.querySelector("[data-la-dn-price]");

        if (!card || !dnWrapper) {
            return;
        }

        if (newPriceEl && priceEl) {
            priceEl.replaceWith(newPriceEl);
        }

        for (const attr in dnWrapper.dataset) {
            card.dataset[attr] = dnWrapper.dataset[attr];
            if (tile) {
                tile.dataset[attr] = dnWrapper.dataset[attr];
            }
        }

        const { comparablePrice } = this.applySortedDisplayPrice(card, tile);

        const renderedPriceText = (
            tile?.querySelector(".dinamic-price")
            || card?.querySelector(".dinamic-price")
            || tile?.querySelector("[data-la-dn-price]")
            || card?.querySelector("[data-la-dn-price]")
        )?.textContent?.trim() || "";

        poPlpCardSortDebug("updateDNPrice", {
            handle: this.handle,
            groupKey: this.groupKey || null,
            currentIndexName: getCurrentPlpIndexName(),
            currentProductUrl: this.currentProductUrl,
            currentVariantHandle: this.currentVariantHandle,
            renderedPriceText,
            comparablePrice: comparablePrice ?? null,
            datasetPrice: card?.dataset?.laDnProductPrice || null,
            datasetPriceMin: card?.dataset?.laDnProductPriceMin || null,
        });

        document.dispatchEvent(new CustomEvent("la:dn:collection:updated", {
            detail: { card }
        }));
    }

    updateCaption (templateContent) {
        const card = this.closest(".po-algolia-product-card");
        const caption = card?.querySelector("[data-caption]");
        const newCaption = templateContent?.querySelector("[data-caption]");

        if (caption && newCaption) {
            caption.replaceWith(newCaption);
        }

        const statusRoot = card?.querySelector(".prod-status");
        const newStatusRoot = templateContent?.querySelector(".prod-status");

        if (statusRoot && newStatusRoot) {
            statusRoot.innerHTML = newStatusRoot.innerHTML;
        } else {
            const status = card?.querySelector("[data-prod-status]");
            const newStatus = templateContent?.querySelector("[data-prod-status]");

            if (status && newStatus) {
                status.replaceWith(newStatus);
            }
        }

        const statusText = card?.querySelector("[data-prod-status]")?.textContent?.trim() ?? "";
        const PoSlider = window.PoSliderComponent;
        if (PoSlider?.applyPlpProdStatus) {
            PoSlider.applyPlpProdStatus(card, statusText);
            const currentThumb = this.getActiveThumbnails()[this.currentPage - 1];
            if (currentThumb) {
                currentThumb.dataset.plpStatus = statusText;
            }
            return;
        }

        const oosTarget = card?.querySelector("[data-plp-oos-target]");
        if (oosTarget) {
            oosTarget.classList.toggle("tw-opacity-50", statusText.length > 0);
        }
    }
});
})();
